import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { Icon } from './Icon';

export interface MenuItem { label: string; icon?: string; onSelect?: () => void | Promise<void>; href?: string; danger?: boolean; testId?: string }

/** Accessible menu button (WAI-ARIA menu pattern): Enter/Space/ArrowDown open, arrows move, Escape closes. */
export function Menu({ label, items, trigger, triggerClass = 'icon-btn', align = 'right', testId }: {
  label: string; items: (MenuItem | null | false)[]; trigger?: ComponentChildren; triggerClass?: string; align?: 'left' | 'right'; testId?: string;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const { route } = useLocation();
  const list = items.filter(Boolean) as MenuItem[];

  const focusItem = (i: number) => {
    const els = root.current?.querySelectorAll<HTMLElement>('[role=menuitem]');
    if (!els?.length) return;
    els[(i + els.length) % els.length]!.focus();
  };
  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => focusItem(0));
    const onDoc = (e: Event) => { if (!root.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('pointerdown', onDoc);
    return () => document.removeEventListener('pointerdown', onDoc);
  }, [open]);

  const close = (refocus = true) => { setOpen(false); if (refocus) btn.current?.focus(); };
  const onMenuKey = (e: KeyboardEvent) => {
    const els = [...(root.current?.querySelectorAll<HTMLElement>('[role=menuitem]') ?? [])];
    const i = els.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); focusItem(i + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); focusItem(i - 1); }
    else if (e.key === 'Home') { e.preventDefault(); focusItem(0); }
    else if (e.key === 'End') { e.preventDefault(); focusItem(els.length - 1); }
    else if (e.key === 'Escape') { e.preventDefault(); close(); }
    else if (e.key === 'Tab') setOpen(false);
  };
  const select = async (it: MenuItem, e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    close(!it.href);
    if (it.href) route(it.href);
    else await it.onSelect?.();
  };

  return (
    <div class="menu" ref={root} onClick={(e) => e.stopPropagation()}>
      <button ref={btn} type="button" class={triggerClass} aria-haspopup="menu" aria-expanded={open} aria-label={label} title={label} data-testid={testId}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(!open); }}
        onKeyDown={(e) => { if (e.key === 'ArrowDown' && !open) { e.preventDefault(); setOpen(true); } }}>
        {trigger ?? <Icon name="more" size={20} />}
      </button>
      {open && (
        <div class={`menu-pop ${align}`} role="menu" aria-label={label} onKeyDown={onMenuKey}>
          {list.map((it) => (
            <a key={it.label} role="menuitem" tabIndex={-1} href={it.href ?? '#'} class={it.danger ? 'danger' : undefined} data-testid={it.testId} onClick={(e) => select(it, e)}>
              {it.icon && <Icon name={it.icon} size={16} />}<span>{it.label}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
